import { SetStateAction, useEffect, useState } from "react";

interface FormBinderState<T> {
  values: T;
  errors: string;
  handleChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => void;
  handleBlur: (
    e: React.FocusEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => void;
  validate: (callback: (values: T) => unknown) => string;
  onSubmitting: (callback: (values: T) => void) => void;
  resetValues: (newValues?: T) => void;
  setValues: (newValues: T) => void;
}

const useFormBinder = <T,>(initialState: T): FormBinderState<T> => {
  const [values, setValues] = useState<T>(initialState);
  const [errors, setErrors] = useState<string>(null);

  useEffect(() => {
    if (values !== initialState) {
      setValues(initialState);
    }
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const prop = e.target.id || e.target.name;

    if (e.target.type === "checkbox") {
      const isChecked = e.target["checked"];
      setValues({
        ...values,
        [prop]: isChecked,
      });
    } else {
      setValues({
        ...values,
        [prop]: e.target.value,
      });
    }
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const prop = e.target.id || e.target.name;
    if (e.target.type === "checkbox") {
      const isChecked = e.target["checked"];
      setValues({
        ...values,
        [prop]: isChecked,
      });
    } else {
      setValues({
        ...values,
        [prop]: e.target.value,
      });
    }
  };

  const validate = (callback: (values: T) => string | null) => {
    const validationErrors = callback(values);
    setErrors(validationErrors);
    return validationErrors;
  };

  const onSubmitting = (callback: (values: T) => void) => {
    return callback(values);
  };

  const resetValues = (newValues?: T) => setValues(newValues ?? initialState);

  return {
    errors,
    values,
    handleChange,
    handleBlur,
    onSubmitting,
    validate,
    resetValues,
    setValues,
  };
};
export default useFormBinder;
